import querystring from 'query-string';

import * as clientErrTypes from 'config/clientErrorTypes';
import {
    MOCK_API_ENDPOINT,
    FEVER_API_ENDPOINT,
    CLIENT_API_FEVER_HOST,
    ZINES_API_ENDPOINT
} from 'config/envVariable';
import { GET, POST, PUT, DELETE, normalizeResponse } from 'api/common';
import tokenManager from 'client/utils/tokenManager';
import { loginTypesForApi } from 'config/loginTypes';
import { NOTIFY_DELIVER_TYPE_MAP } from 'config/notifyTypes';
import { generateApiErrObj } from 'client/hooks/useClientErrorHandler';
import {
    GET_PRIZE_WINNER_STATUS_API_RESPONSE,
    GET_USER_POINTS_API_RESPONSE,
    STORE_GAME_RESULT_API_RESPONSE,
    POST_NOTIFY_API_RESPONSE
} from 'apiHandler/client';
import { isJSONParsable } from 'utils/objectManipulate';

export function getTestQueryString() {
    const { test_code, test_expired } = querystring.parse(window.location.search);
    return test_code && test_expired
        ? { 'preview-code': test_code, 'expired-at': test_expired }
        : null;
}

export function throwError(resp, throwErrConfigs = [], body = '') {
    const configs = Object.entries(throwErrConfigs);

    for (const [errType, checker] of configs) {
        if (typeof checker === 'function' && checker(resp)) {
            const apiBody = isJSONParsable(body) ? JSON.parse(body) : body;

            throw generateApiErrObj(resp, errType, apiBody);
        }
    }
}

export async function fetches(
    path,
    optionsGetter,
    { passPayload = false, dataKeys, throwErrConfigs, isMock = false } = {}
) {
    const options = typeof optionsGetter === 'function' ? await optionsGetter() : optionsGetter;
    const baseUrl = isMock ? MOCK_API_ENDPOINT : FEVER_API_ENDPOINT;
    return fetch(`${baseUrl}${path}`, options)
        .then(normalizeResponse)
        .then(({ httpStatus, payload, headers }) => {
            const resp = {
                httpStatus,
                method: options.method,
                headers,
                body: Boolean(options.body) ? JSON.stringify(options.body) : null,
                payload
            };

            throwError(resp, throwErrConfigs, options.body);
            if (resp.httpStatus === 200 && Boolean(dataKeys)) {
                resp.payload = payload[dataKeys];
            }
            if (resp.httpStatus === 200 && Boolean(passPayload)) {
                if (Boolean(dataKeys)) return payload[dataKeys];
                else return payload;
            }
            return resp;
        });
}

export function generateFetchOpts({
    method = GET,
    headers = {},
    body = null,
    formData,
    apiVersion = 1,
    token,
    feverHost,
    ...rest
} = {}) {
    return {
        method,
        headers: new Headers({
            'Fever-Host': CLIENT_API_FEVER_HOST,
            ...(feverHost ? { 'Fever-Host': feverHost } : { 'Fever-Host': CLIENT_API_FEVER_HOST }),
            Accept: `application/x.api.v${apiVersion}+json`,
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(formData ? {} : { 'Content-Type': 'application/json; charset=utf-8' }),
            ...headers
        }),
        ...(method !== GET ? { body: formData ? formData : body && JSON.stringify(body) } : {}),
        ...rest
    };
}

export function generateFetchOptsGetter(options) {
    return async () => {
        const { token, errorResp, loginType } = await tokenManager.getAccessToken();

        if (errorResp) {
            throw generateApiErrObj(errorResp);
        }

        return loginType === loginTypesForApi.INSTAGRAM
            ? generateFetchOpts({ ...options, headers: { 'Fever-Entry-Token': token } })
            : generateFetchOpts({ ...options, token });
    };
}

export function getFormClientData({ formId, isPreview = false }) {
    const qsObj = {};
    if (isPreview) {
        qsObj.mode = 'preview';
    }
    const qs = querystring.stringify(qsObj);
    return fetches(`/forms/${formId}${qs ? `?${qs}` : ''}`, generateFetchOpts(), {
        dataKeys: 'items',
        throwErrConfigs: {
            [clientErrTypes.NOT_FOUND]: r => r.httpStatus === 404,
            [clientErrTypes.GET_API_ERROR]: r => r.httpStatus !== 200
        }
    });
}

export function getRedirectFormUrl({ formId, entryUuid, promoId }) {
    const qs = querystring.stringify({ promotionId: promoId, entryUuid });
    return fetches(
        `/forms/${formId}/redirect-url${qs ? `?${qs}` : ''}`,
        generateFetchOptsGetter(),
        {
            dataKeys: 'items',
            throwErrConfigs: {
                [clientErrTypes.NOT_FOUND]: r => r.httpStatus === 404,
                [clientErrTypes.GET_API_ERROR]: r => r.httpStatus !== 200
            }
        }
    );
}

export function uploadFormAttachFile(file) {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return fetches('/uploads', generateFetchOpts({ method: POST, formData }), {
        dataKeys: 'items'
    });
}

export function submitForm(formId, payload, headers, redeemToken) {
    const testQs = getTestQueryString();
    const qs = testQs ? `?${querystring.stringify(testQs)}` : '';
    const fetchOpts = Boolean(redeemToken)
        ? generateFetchOpts({
              method: POST,
              body: { ...payload, redeemToken },
              headers
          })
        : generateFetchOptsGetter({
              method: POST,
              body: payload,
              headers
          });
    return fetches(`/forms/${formId}/submit${qs}`, fetchOpts, {
        dataKeys: 'items',
        throwErrConfigs: {
            [clientErrTypes.STORE_GAME_RESULT_ENTRY_FLOW_EXISTS]: r =>
                STORE_GAME_RESULT_API_RESPONSE.ENTRY_FLOW_EXISTS(r?.payload || {})
        }
    });
}

export function checkTrackingStatus({ promoUUID, entriesUUID }) {
    return fetches(
        `/promotions/${promoUUID}/entries/${entriesUUID}/tracks`,
        generateFetchOptsGetter()
    );
}

export function checkIsQualifyToGetWinner({ promoUUID, entriesUUID, lotteryUUID } = {}) {
    const testQs = getTestQueryString();
    const qs = testQs ? `?${querystring.stringify(testQs)}` : '';
    return fetches(
        `/promotions/${promoUUID}/entries/${entriesUUID}/validation/lotteries/${lotteryUUID}${qs}`,
        generateFetchOptsGetter({ method: POST }),
        {
            dataKeys: 'data',
            throwErrConfigs: {
                [clientErrTypes.GET_WINNER_QUALIFY]: r =>
                    r.httpStatus !== 200 && r.httpStatus !== 226
            }
        }
    );
}

export function getWinner(promoUUID, entriesUUID, lotteryUUID, pickPrizes) {
    const testQs = getTestQueryString();
    const qs = testQs ? `?${querystring.stringify(testQs)}` : '';
    const options = { method: POST };

    if (pickPrizes) {
        options.body = { pickPrizes };
    }

    return fetches(
        `/promotions/${promoUUID}/entries/${entriesUUID}/winner${
            lotteryUUID ? `/${lotteryUUID}` : ''
        }${qs}`,
        generateFetchOptsGetter(options),
        { dataKeys: 'data' }
    );
}

export function updateWinnerAnimationFlag(promoUUID, entryUUID, prizeUUID) {
    const options = {
        body: {
            prizes: Array.isArray(prizeUUID) ? prizeUUID : [prizeUUID]
        },
        method: PUT
    };

    return fetches(
        `/promotions/${promoUUID}/entries/${entryUUID}/winner/prizes/animation`,
        generateFetchOptsGetter(options),
        {
            throwErrConfigs: {
                [clientErrTypes.PRIZE_OPEN_WINNER]: r => r.httpStatus !== 200
            }
        }
    );
}

export function updateEntryFlowLotteryParticipant({ promoUUID, entryUUID, lotteryUUID }) {
    return fetches(
        `/promotions/${promoUUID}/entries/${entryUUID}/lotteries/${lotteryUUID}`,
        generateFetchOptsGetter({ method: PUT }),
        {
            throwErrConfigs: {
                [clientErrTypes.PRIZE_OPEN_WINNER]: r => r.httpStatus !== 200
            }
        }
    );
}

export function getEntriesListApi(promoUUID) {
    return fetches(`/promotions/${promoUUID}/entries?ts=${Date.now()}`, generateFetchOptsGetter(), {
        dataKeys: 'data',
        throwErrConfigs: {
            [clientErrTypes.GET_HAS_GAME_RESULT_RECORDS]: r => r.httpStatus !== 200
        }
    });
}

export function getVoteResultApi(formId) {
    return fetches(`/forms/${formId}/votes`, generateFetchOptsGetter(), { dataKeys: 'items' });
}

export function getPrizeListApi(promoUUID) {
    return fetches(`/promotions/${promoUUID}/winners`, generateFetchOptsGetter(), {
        dataKeys: 'data',
        throwErrConfigs: {
            [clientErrTypes.GET_API_ERROR]: r => r.httpStatus !== 200
        }
    });
}

export function getSponsorPrizeListApi(sponsorUUID, loginType) {
    return fetches(
        `/promotions/members/${sponsorUUID}/prizes/${loginType}`,
        generateFetchOptsGetter(),
        {
            dataKeys: 'data',
            throwErrConfigs: {
                [clientErrTypes.GET_API_ERROR]: r => r.httpStatus !== 200
            }
        }
    );
}

export function getPrizeInfoAPI(promoUUID, prizeUUID) {
    return fetches(`/promotions/${promoUUID}/prizes/${prizeUUID}`, generateFetchOpts(), {
        dataKeys: 'data',
        throwErrConfigs: { [clientErrTypes.GET_API_ERROR]: r => r.httpStatus !== 200 }
    });
}

export function getPrizeWinnerStatusAPI(promoUUID, entryUUID, prizeUUID) {
    return fetches(
        `/promotions/${promoUUID}/entries/${entryUUID}/winner/prizes/${prizeUUID}`,
        generateFetchOpts(),
        {
            dataKeys: 'data',
            throwErrConfigs: {
                [clientErrTypes.INVALID_PRIZE_PAGE_WINNER_INFO]: r =>
                    r.httpStatus === 500 && Number(r.payload.status_code) === 500,
                [clientErrTypes.WINNER_NOT_FOUND]: r =>
                    GET_PRIZE_WINNER_STATUS_API_RESPONSE.WINNER_NOT_FOUND(r?.payload || {}),
                [clientErrTypes.PRIZE_PAGE_WINNER_INFO]: r => r.httpStatus !== 200
            }
        }
    );
}

export function getWinnerPrizesByEntry({ promoUUID, entryUUID, lotteryUUID }) {
    return fetches(
        `/promotions/${promoUUID}/entries/${entryUUID}/winner/${lotteryUUID}?ts=${Date.now()}`,
        generateFetchOptsGetter(),
        {
            dataKeys: 'data',
            throwErrConfigs: { [clientErrTypes.PRIZE_PAGE_WINNER_INFO]: r => r.httpStatus !== 200 }
        }
    );
}

export function getRemainPrizeAPI(promoUUID, lotteryUUID) {
    return fetches(
        `/promotions/${promoUUID}/lotteries/${lotteryUUID}/remainder`,
        generateFetchOpts(),
        {
            dataKeys: 'data',
            throwErrConfigs: { [clientErrTypes.GET_API_ERROR]: r => r.httpStatus !== 200 }
        }
    );
}

export function sendMailAPI(promoUUID, entriesUUID, prizeUUID, body) {
    return fetches(
        `/promotions/${promoUUID}/entries/${entriesUUID}/winner/prizes/${prizeUUID}/notification`,
        generateFetchOptsGetter({ method: POST, body }),
        { dataKeys: 'data' }
    );
}

export function getRedeemPrizeUrlAPI(promoUUID, entriesUUID, prizeUUID) {
    return fetches(
        `/promotions/${promoUUID}/entries/${entriesUUID}/winner/prizes/${prizeUUID}/redeem-link?domain=${encodeURIComponent(
            window.location.origin
        )}`,
        generateFetchOptsGetter(),
        { dataKeys: 'data' }
    );
}

export function storeRedeemAPI(promoUUID, entriesUUID, prizeUUID, redeemToken) {
    const fetchOptions = Boolean(redeemToken)
        ? generateFetchOpts({ method: PUT, body: { redeemToken } })
        : generateFetchOptsGetter({ method: PUT });
    return fetches(
        `/promotions/${promoUUID}/entries/${entriesUUID}/winner/prizes/${prizeUUID}/redeem`,
        fetchOptions,
        { dataKeys: 'data' }
    );
}

export function getPrizeVisualNumber(promoUUID, entriesUUID, prizeUUID) {
    return fetches(
        `/promotions/${promoUUID}/entries/${entriesUUID}/winner/prizes/${prizeUUID}/virtual-number`,
        generateFetchOpts()
    );
}

export function getPartnerSponsorInfo(sponsorUUID) {
    return fetches(`/promotions/sponsors/${sponsorUUID}/info`, generateFetchOpts(), {
        dataKeys: 'data',
        throwErrConfigs: {
            [clientErrTypes.GET_API_ERROR]: r => r.httpStatus !== 200
        }
    });
}

export function getPromoInfo({ promoUUID, promoId, feverHost } = {}) {
    return fetches(`/promotions/${promoUUID ?? promoId}`, generateFetchOpts({ feverHost }), {
        dataKeys: 'data',
        throwErrConfigs: {
            [clientErrTypes.NOT_FOUND]: r => r.httpStatus === 400 && r?.payload?.message === '404',
            [clientErrTypes.GET_API_ERROR_AND_NO_REFRESH_BTN]: r => r.httpStatus === 403,
            [clientErrTypes.GET_API_ERROR]: r => r.httpStatus !== 200
        }
    });
}

export function getPromoInfoApi({ promoUUID, promoId, feverHost } = {}) {
    return fetches(`/promotions/${promoUUID ?? promoId}/infos`, generateFetchOpts({ feverHost }), {
        dataKeys: 'data',
        passPayload: true,
        throwErrConfigs: {
            [clientErrTypes.PROMO_NOT_FOUND]: r =>
                r.httpStatus === 400 && r?.payload?.code === 'PMM-R1-FNF1',
            [clientErrTypes.GET_API_ERROR]: r => r.httpStatus !== 200
        }
    });
}

export function getFormInfo({ formId, isPreview }) {
    return getFormClientData({ formId, isPreview });
}

export function getPromoLotteries(promoUUID) {
    return fetches(`/promotions/${promoUUID}/lotteries`, generateFetchOpts(), {
        dataKeys: 'data',
        throwErrConfigs: { [clientErrTypes.GET_API_ERROR]: r => r.httpStatus !== 200 }
    });
}

export function getLotteryInfo(promoUUID, lotteryUuid) {
    return fetches(`/promotions/${promoUUID}/lotteries/${lotteryUuid}`, generateFetchOpts(), {
        dataKeys: 'data',
        throwErrConfigs: { [clientErrTypes.GET_API_ERROR]: r => r.httpStatus !== 200 }
    });
}

export function getLotteryPrizes(promoUUID, lotteryUuid) {
    return fetches(
        `/promotions/${promoUUID}/lotteries/${lotteryUuid}/prizes`,
        generateFetchOpts(),
        {
            dataKeys: 'data',
            throwErrConfigs: { [clientErrTypes.GET_API_ERROR]: r => r.httpStatus !== 200 }
        }
    );
}

export function getUserLatestEntries(promoUUID) {
    return fetches(
        `/promotions/${promoUUID}/entries/latest?ts=${Date.now()}`,
        generateFetchOptsGetter(),
        {
            dataKeys: 'data',
            throwErrConfigs: { [clientErrTypes.GET_USER_LATEST_ENTRIES]: r => r.httpStatus !== 200 }
        }
    );
}

export function createUserEntries(promoUUID, recaptchaToken) {
    const testQs = getTestQueryString();
    const qs = testQs ? `?${querystring.stringify(testQs)}` : '';

    return fetches(
        `/promotions/${promoUUID}/entries${qs}`,
        generateFetchOptsGetter({
            method: POST,
            ...(Boolean(recaptchaToken) ? { headers: { recaptchaToken } } : {})
        }),
        { dataKeys: 'data' }
    );
}

export function verifyLineFriend(promoUUID) {
    return fetches(`/promotions/${promoUUID}/line/friendship/status`, generateFetchOptsGetter(), {
        dataKeys: 'data',
        throwErrConfigs: {
            [clientErrTypes.LINE_FRIEND_QUALIFY_VALIDATION_FAILED]: r => r.httpStatus !== 200
        }
    });
}

export function verifyKKboxMemberQualify(promoUUID) {
    return fetches(`/promotions/${promoUUID}/verify`, generateFetchOptsGetter(), {
        dataKeys: 'data'
    });
}

export function verityMemberQualification(promoUUID) {
    return fetches(`/promotions/${promoUUID}/qualification/verify`, generateFetchOptsGetter(), {
        dataKeys: 'data'
    });
}
// kkpoint
export function checkKKUserExchangePointsStatus(promoUUID, entryUUID) {
    return fetches(
        `/promotions/${promoUUID}/entries/${entryUUID}/point/status`,
        generateFetchOptsGetter()
    );
}

export function getKKboxUserPoints(promoUUID, entryUUID) {
    return fetches(
        `/promotions/${promoUUID}/entries/${entryUUID}/point`,
        generateFetchOptsGetter()
    );
}

export function exchangeKKboxPoints(promoUUID, entryUUID) {
    return fetches(
        `/promotions/${promoUUID}/entries/${entryUUID}/point/cost`,
        generateFetchOptsGetter({ method: POST })
    );
}
// General point
export function getUserPoints(promoUUID, entryUUID) {
    return fetches(
        `/promotions/${promoUUID}/entries/${entryUUID}/transactions/user/points`,
        generateFetchOptsGetter(),
        {
            dataKeys: 'data',
            throwErrConfigs: {
                [clientErrTypes.THIRD_PARTY_API_TIMED_OUT]: r =>
                    GET_USER_POINTS_API_RESPONSE.THIRD_PARTY_API_TIMED_OUT(r?.payload)
            }
        }
    );
}

export function exchangePoints(promoUUID, entryUUID) {
    return fetches(
        `/promotions/${promoUUID}/entries/${entryUUID}/transactions/withdraw`,
        generateFetchOptsGetter({ method: POST })
    );
}

export function validateInvoiceApi({ promoUUID, entryUUID, body }) {
    return fetches(
        `/promotions/${promoUUID}/entries/${entryUUID}/validation/invoice`,
        generateFetchOptsGetter({ method: POST, body })
    );
}

export function validateTestCodeAPI({ host, sponsorId, testCode, testExpired } = {}) {
    return fetch(
        `${ZINES_API_ENDPOINT}/sponsors/${sponsorId}/verify-preview-code?host=${host}&preview-code=${testCode}&expired-at=${testExpired}`,
        {
            method: 'GET'
        }
    ).then(normalizeResponse);
}

export function uploadPhoto({
    promoUUID,
    entryUUID,
    file,
    compressType,
    seq,
    mediaUrl,
    mediaSource
}) {
    const formData = new FormData();

    // 傳遞 img URL 讓後端去抓該 URL img 再存到 S3 避免有些圖 CORS 前端無法轉為 blob 傳給後端
    if (Boolean(mediaUrl) && Boolean(mediaSource)) {
        formData.append('mediaUrl', mediaUrl);
        formData.append('mediaSource', mediaSource);
    } else {
        formData.append('image', file, file.name);
    }
    formData.append('compressType', compressType);
    formData.append('seq', seq);
    return fetches(
        `/promotions/${promoUUID}/entries/${entryUUID}/media`,
        generateFetchOptsGetter({ method: POST, formData })
    );
}

export function submitPromo({ method = POST, promoUUID, entryUUID, headers, body }) {
    const testQs = getTestQueryString();
    const qs = testQs ? `?${querystring.stringify(testQs)}` : '';
    return fetches(
        `/promotions/${promoUUID}/entries/${entryUUID}/result${qs}`,
        generateFetchOptsGetter({ method, headers, body }),
        {
            throwErrConfigs: {
                [clientErrTypes.STORE_GAME_RESULT_ENTRY_FLOW_EXISTS]: r =>
                    STORE_GAME_RESULT_API_RESPONSE.ENTRY_FLOW_EXISTS(r?.payload || {})
            }
        }
    );
}

export function getUserPasscodeStatus({ promoUUID, entryUUID }) {
    return fetches(
        `/promotions/${promoUUID}/entries/${entryUUID}/passcode/status`,
        generateFetchOpts()
    );
}

export function getPasscodeVerify({ promoUUID, passcode, recaptchaToken }) {
    return fetches(
        `/promotions/${promoUUID}/passcode/${passcode}/verify`,
        generateFetchOpts({
            ...(Boolean(recaptchaToken) ? { headers: { recaptchaToken } } : {})
        })
    );
}

export function submitPasscode({ promoUUID, entryUUID, passcode }) {
    return fetches(
        `/promotions/${promoUUID}/entries/${entryUUID}/passcode`,
        generateFetchOpts({ method: POST, body: { passcode } }),
        {
            dataKeys: 'data'
        }
    );
}

export function getPromoClientOptionList({ promoUUID }) {
    return fetches(`/promotions/${promoUUID}/options`, generateFetchOpts(), {
        dataKeys: 'data',
        throwErrConfigs: { [clientErrTypes.GET_API_ERROR]: r => r.httpStatus !== 200 }
    });
}

export function startGame({ promoUUID, entryUUID }) {
    return fetches(
        `/promotions/${promoUUID}/entries/${entryUUID}/game/start`,
        generateFetchOptsGetter({ method: POST }),
        {
            throwErrConfigs: {
                [clientErrTypes.MEMORY_MATCH_START_GAME_FAILED]: r => r.httpStatus !== 200
            }
        }
    );
}

export function getEntryFlow({ promoUUID, entryUUID }) {
    return fetches(
        `/promotions/${promoUUID}/entries/${entryUUID}/flows`,
        generateFetchOptsGetter()
    );
}

export function getPromoTaskListApi({ promoUUID }) {
    return fetches(`/promotions/${promoUUID}/tasks`, generateFetchOpts(), {
        dataKeys: 'data',
        throwErrConfigs: { [clientErrTypes.GET_API_ERROR]: r => r.httpStatus !== 200 }
    });
}

// 後端追蹤 user 在 IG 聊天室點了哪些系統發送帶有 passcode 的活動網址
export function trackCampaignClick({ pcode }) {
    return fetches(
        `/promotions/campaigns/click?pcode=${pcode}`,
        generateFetchOpts({ method: POST }),
        {
            dataKeys: 'data',
            throwErrConfigs: { [clientErrTypes.GET_API_ERROR]: r => r.httpStatus !== 200 }
        }
    );
}

/**
 * @method redeemPointsApi 取得活動使用的點數池清單
 * @param  {string} promoUUID 活動 Uuid
 * @param  {string} entryUUID entry Uuid
 * @param  {string} lotteryUUID 獎項 Uuid
 * @param  {string} prizeUUID 獎品 Uuid
 * @return  {promise}
 */
export function redeemPointsApi({ promoUUID, entryUUID, lotteryUUID, prizeUUID }) {
    return fetches(
        `/promotions/${promoUUID}/entries/${entryUUID}/lotteries/${lotteryUUID}/prizes/${prizeUUID}/member/points/cost`,
        generateFetchOptsGetter({
            method: POST
        }),
        {
            dataKeys: 'data',
            throwErrConfigs: { [clientErrTypes.REDEEM_POINT_FAILED]: r => r.httpStatus !== 200 }
        }
    );
}

/**
 * @method submitReferralCodeApi 送出邀請碼
 * @param  {string} promoUuid    活動 Uuid
 * @param  {string} entryUuid    Entry Uuid
 * @param  {string} referralCode 邀請碼
 * @return  {promise}
 */
export function submitReferralCodeApi({ promoUuid, entryUuid, referralCode }) {
    return fetches(
        `/promotions/${promoUuid}/entries/${entryUuid}/referral-code/${referralCode}`,
        generateFetchOptsGetter({ method: POST }),
        {
            dataKeys: 'data'
        }
    );
}
/**
 * @method checkReferralCodeApi 檢查邀請碼
 * @param  {string} promoUuid    活動 Uuid
 * @param  {string} referralCode 邀請碼
 * @return  {promise}
 */
export function checkReferralCodeApi({ promoUuid, referralCode }) {
    return fetches(`/promotions/${promoUuid}/referral-code/${referralCode}`, generateFetchOpts(), {
        dataKeys: 'data'
    });
}

/**
 * @method getPromoReferralInfoApi get 推薦邀請碼 & 短網址 & 推薦成功人數 api
 * @param  {string} promoUUID 活動 Uuid
 * @return  {promise}
 */
export function getPromoReferralInfoApi(promoUUID) {
    return fetches(`/promotions/${promoUUID}/referral-code`, generateFetchOptsGetter(), {
        dataKeys: 'data',
        throwErrConfigs: { [clientErrTypes.GET_API_ERROR]: r => r.httpStatus !== 200 }
    });
}

/**
 * @method getShareInfoApi 取得這檔活動的 OG 資料與 fullname
 * @description https://app.clickup.com/t/860rv2g09
 * @param  {string} promoUUID 活動 Uuid
 * @param  {string=} entryUUID Entry Uuid，用來取對應的邀請人 fullname 或是 share data
 * @param  {string=} referralCode 邀請碼，用來取對應的邀請人 fullname
 * @return  {promise}
 */
export function getShareInfoApi({ promoUUID, entryUUID = undefined, referralCode = undefined }) {
    const args = {
        entryUuid: entryUUID,
        referralCode
    };
    const params = new URLSearchParams();
    // Set query parameters, automatically excluding undefined values
    for (const key in args) {
        if (args[key] !== undefined) {
            params.set(key, args[key]);
        }
    }
    const qs = params.toString();
    return fetches(
        `/promotions/${promoUUID}/share-info${qs ? `?${qs}` : ''}`,
        generateFetchOpts(),
        {
            dataKeys: 'data'
        }
    );
}

export function getShortLinkApi(promoUUID) {
    return fetches(`/promotions/${promoUUID}/short-link`, generateFetchOptsGetter(), {
        dataKeys: 'data'
    });
}

/**
 * @method getNotificationApi 取得通知 api
 * @description https://app.clickup.com/t/860ru7a42
 * @param  {string} promoUUID 活動 Uuid
 * @return  {promise}
 */
export function getNotificationApi(promoUUID) {
    return fetches(`/promotions/${promoUUID}/notifications`, generateFetchOptsGetter(), {
        dataKeys: 'data'
    });
}

/**
 * @method createNotificationApi 新增訂閱活動通知+取授權 api
 * @description https://app.clickup.com/t/860ru21af
 * @param  {string} promoUUID 活動 Uuid
 * @param  {string} sourceType 通知方式來源的類型 (1.後台 admin setting 的 email / 2.登入帳號的 email / 3.表單填寫的 email / 4.在前臺手動輸入的 email)
 * @param  {string} driver 通知的管道 (messageBirdEmail / lineNotify)
 * @param  {string} code line notify api 回傳的 code
 * @param  {string} email email address
 * @return  {promise}
 */
export function createNotificationApi({ promoUUID, sourceType, driver, code, email, redirectUrl }) {
    const subscriptionsPayload =
        driver === NOTIFY_DELIVER_TYPE_MAP.EMAIL ? { email } : { code, redirectUrl };
    const body = { sourceType, driver, ...subscriptionsPayload };
    return fetches(
        `/promotions/${promoUUID}/notifications`,
        generateFetchOptsGetter({ method: POST, body }),
        {
            dataKeys: 'data',
            throwErrConfigs: {
                [clientErrTypes.NOTIFY_MEMBER_NOT_FOUND]: r =>
                    POST_NOTIFY_API_RESPONSE.MEMBER_NOT_FOUND(r?.payload || {}),
                [clientErrTypes.NOTIFY_EMAIL_NOT_VALID]: r =>
                    POST_NOTIFY_API_RESPONSE.MEMBER_EMAIL_NOT_VALID(r?.payload || {}),
                [clientErrTypes.NOTIFY_DRIVER_NOT_SUPPORT]: r =>
                    POST_NOTIFY_API_RESPONSE.DRIVER_NOT_SUPPORT(r?.payload || {}),
                [clientErrTypes.NOTIFY_CREATE_FAILED]: r =>
                    POST_NOTIFY_API_RESPONSE.CREATE_NOTIFICATION_FAILED(r?.payload || {}),
                [clientErrTypes.NOTIFY_MISSING_REQUIRED_FIELDS]: r =>
                    POST_NOTIFY_API_RESPONSE.MISSING_NOTIFICATION_REQUIRED_FIELDS(r?.payload || {})
            }
        }
    );
}

/**
 * @method deleteNotificationApi 取消訂閱活動通知 api
 * @description https://app.clickup.com/t/860ru21zm
 * @param  {string} promoUUID 活動 Uuid
 * @return  {promise}
 */
export function deleteNotificationApi(promoUUID) {
    return fetches(
        `/promotions/${promoUUID}/notifications`,
        generateFetchOptsGetter({ method: DELETE }),
        {
            dataKeys: 'data'
        }
    );
}

/**
 * @method getChallengeRecordCard 取得闖關卡
 * @description https://app.clickup.com/t/86epzk6w8
 * @param  {string} promoUUID 活動 Uuid
 * @return  {promise}
 */
export function getChallengeRecordCard(promoUUID) {
    return fetches(`/promotions/${promoUUID}/check-point-card`, generateFetchOptsGetter(), {
        dataKeys: 'data'
    });
}

/**
 * @method checkTaskCompletedRecord 檢查任務完成紀錄
 * @description https://app.clickup.com/t/86epme2ky
 * @param  {string} promoUUID 活動 Uuid
 * @param  {string} taskUUID  關卡 Uuid
 * @return  {promise}
 */
export function checkTaskCompletedRecord(promoUUID, taskUUID) {
    return fetches(
        `/promotions/${promoUUID}/tasks/${taskUUID}`,
        generateFetchOptsGetter({ method: POST }),
        {
            dataKeys: 'data'
        }
    );
}

/**
 * @method getOrderListApi 取得訂單列表 api
 * @description https://app.clickup.com/t/86epvyee1
 * @param  {string} promoUUID 活動 UUID
 * @param  {number} perPage 每頁幾筆
 * @param  {number} page 第幾頁
 * @return  {promise}
 */
export function getMemberOrderListApi({ promoUUID, perPage = 10, page }) {
    const qs = querystring.stringify({ perPage, page });

    return fetches(
        `/promotions/${promoUUID}/member/orders${qs ? `?${qs}` : ''}`,
        generateFetchOptsGetter({ method: GET })
    );
}

/**
 * @method getOrderListApi 特定 entry 的通過條件和 prize
 * @description https://app.clickup.com/t/86erz6v46
 * @param  {string} promoUUID 活動 UUID
 * @param  {string} entryUuid  entry UUID
 * @return  {promise}
 */
export function getEntryPassTasksFilterListApi({ promoUUID, entryUuid }) {
    return fetches(
        `/promotions/${promoUUID}/entries/${entryUuid}/prizes`,
        generateFetchOptsGetter({ method: GET })
    );
}

/**
 * @method getReceiptItemListApi 取得 Receipt Item API
 * @description https://app.clickup.com/t/86et2w2d1
 * @param  {string} promoUUID 活動 UUID
 * @param  {string} entryUUID entry UUID
 * @return  {promise}
 */
export function getReceiptItemListApi({ promoUUID, entryUUID }) {
    return fetches(
        `/promotions/${promoUUID}/entries/${entryUUID}/result`,
        generateFetchOptsGetter({ method: GET })
    );
}



/**
 * @method getFormDataApi 取得表單資料 API
 * @description https://app.clickup.com/t/86eteaypg
 * @param  {string} formId 表單 ID
 * @param  {string} entryUuid 參加者 UUID
 * @param  {string} winnerUuid 得獎者 UUID
 * @return  {promise}
 */
export function getFormDataApi({ formId, entryUuid, winnerUuid }) {
    const params = { entryUuid, winnerUuid };
    const queryString = querystring.stringify(params, { skipNull: true });
    
    const url = `/forms/${formId}/member/answers${queryString ? `?${queryString}` : ''}`;
    return fetches(url, generateFetchOptsGetter({ method: GET }));
}
